import type { FeatureFlagValue } from '@posthog/core';
import { PostHog } from 'posthog-js';
import { createContext, useContext } from 'react';

enum PostHogFeatureFlagMap {
  EnableAdditionalExpressOnboardingOptions = 'additional-express-onboarding-options-enabled',
  EnableAgGridUpgrade = 'ag-grid-upgrade',
  EnableAiWorkspaceCustomization = 'ai-workspace-customization',
  EnableAlphaApps = 'enable-alpha-apps',
  EnableAppBuilder = 'enable_app_builder',
  EnableAppBuilderInspector = 'app_builder_inspector',
  EnableBetaApps = 'enable-beta-apps',
  EnableCompanyImport = 'enable_company_import',
  EnableCompanyResetPasswords = 'enable_company_reset_passwords',
  EnableContextualActions = 'enable-contextual-actions',
  EnableContractTemplatesPerf = 'contract-templates-perf',
  EnableIUAccessControl = 'iu_access_control',
  EnableMicrodepositVerification = 'enable-microdeposit-verification',
  EnableMessageAppNotification = 'message-app-notification',
  EnableMessageEditorInput = 'message-editor-input',
  EnableOtherMessagesEditor = 'enable-other-messages-editor',
  EnableDuplicateFileName = 'duplicate_file_name',
  EnableFileBatchActions = 'file_batch_actions',
  EnableFilesAppPerformance = 'files-app-performance',
  EnableLoadFileChannels = 'details-page-file-channels-loading',
  EnableNewEditorFormatter = 'new-editor-formatter',
  EnableInvoiceAddressCustomFields = 'invoice_address_custom_fields',
  EnableAuditLogs = 'audit_logs',
  EnableAuditLogPlatform = 'audit_logs_platform',
  EnableFeatureAppVisibility = 'app_visibility_ocmc_support',
  EnableEventsApiDateFilters = 'events_api_date_filters',
  EnableInvoiceRefunds = 'enable_invoice_refunds',
  EnableClientNotificationsMute = 'enable_client_notifications_mute',
  EnableMcpDashboardUi = 'mcp_dashboard_ui',
  EnableIterateOnProjectGithubActions = 'iterate_on_project_github_actions',
  EnableProductAgent = 'enable_product_agent',
  EnableConsistentAppSettingsLink = 'enable_consistent_app_settings_link',
  EnableMultiInstanceApps = 'enable_multi_instance_apps',
}

export type FlagContextType = Record<
  'EnableManyCompanies' | keyof typeof PostHogFeatureFlagMap,
  boolean
>;

export const DefaultFlagsContext: FlagContextType = {
  // Permanently enabled
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: false,
  EnableAgGridUpgrade: false,
  EnableAiWorkspaceCustomization: false,
  EnableAlphaApps: false,
  EnableAppBuilder: false,
  EnableAppBuilderInspector: false,
  EnableBetaApps: false,
  EnableCompanyImport: false,
  EnableCompanyResetPasswords: false,
  EnableContextualActions: false,
  EnableContractTemplatesPerf: false,
  EnableDuplicateFileName: false,
  EnableFileBatchActions: false,
  EnableFilesAppPerformance: false,
  EnableIUAccessControl: false,
  EnableLoadFileChannels: false,
  EnableMessageAppNotification: false,
  EnableMessageEditorInput: false,
  EnableMicrodepositVerification: false,
  EnableNewEditorFormatter: false,
  EnableOtherMessagesEditor: false,
  EnableInvoiceAddressCustomFields: false,
  EnableInvoiceRefunds: false,
  EnableClientNotificationsMute: false,
  EnableAuditLogs: false,
  EnableEventsApiDateFilters: false,
  EnableFeatureAppVisibility: false,
  EnableMcpDashboardUi: false,
  EnableIterateOnProjectGithubActions: false,
  EnableProductAgent: false,
  EnableAuditLogPlatform: false,
  EnableConsistentAppSettingsLink: false,
  EnableMultiInstanceApps: false,
};

/**
 * These flags are available on the server side, but are not used so we
 * purposefully exclude them
 */
type ClientOnlyFeatureFlagKeysType = never;

export const getServerFlagsContextValue = (
  posthogFlags: Record<PostHogFeatureFlagMap, FeatureFlagValue>,
): Record<
  Exclude<keyof FlagContextType, ClientOnlyFeatureFlagKeysType>,
  boolean
> => ({
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: Boolean(
    posthogFlags[
      PostHogFeatureFlagMap.EnableAdditionalExpressOnboardingOptions
    ],
  ),
  EnableAgGridUpgrade: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAgGridUpgrade],
  ),
  EnableAiWorkspaceCustomization: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAiWorkspaceCustomization],
  ),
  EnableAlphaApps: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableAlphaApps]),
  EnableAppBuilder: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilder],
  ),
  EnableAppBuilderInspector: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilderInspector],
  ),
  EnableBetaApps: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableBetaApps]),
  EnableCompanyImport: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableCompanyImport],
  ),
  EnableCompanyResetPasswords: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableCompanyResetPasswords],
  ),
  EnableContextualActions: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableContextualActions],
  ),
  EnableContractTemplatesPerf: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableContractTemplatesPerf],
  ),
  EnableIUAccessControl: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableIUAccessControl],
  ),
  EnableMicrodepositVerification: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMicrodepositVerification],
  ),
  EnableMessageAppNotification: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMessageAppNotification],
  ),
  EnableMessageEditorInput: Boolean(posthogFlags['message-editor-input']),
  EnableOtherMessagesEditor: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableOtherMessagesEditor],
  ),
  EnableDuplicateFileName: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableDuplicateFileName],
  ),
  EnableFileBatchActions: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableFileBatchActions],
  ),
  EnableFilesAppPerformance: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableFilesAppPerformance],
  ),
  EnableLoadFileChannels: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableLoadFileChannels],
  ),
  EnableNewEditorFormatter: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableNewEditorFormatter],
  ),
  EnableInvoiceAddressCustomFields: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableInvoiceAddressCustomFields],
  ),
  EnableInvoiceRefunds: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableInvoiceRefunds],
  ),
  EnableClientNotificationsMute: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableClientNotificationsMute],
  ),
  EnableAuditLogs: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableAuditLogs]),
  EnableFeatureAppVisibility: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableFeatureAppVisibility],
  ),
  EnableEventsApiDateFilters: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableEventsApiDateFilters],
  ),
  EnableMcpDashboardUi: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMcpDashboardUi],
  ),
  EnableIterateOnProjectGithubActions: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableIterateOnProjectGithubActions],
  ),
  EnableProductAgent: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableProductAgent],
  ),
  EnableAuditLogPlatform: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAuditLogPlatform],
  ),
  EnableConsistentAppSettingsLink: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableConsistentAppSettingsLink],
  ),
  EnableMultiInstanceApps: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMultiInstanceApps],
  ),
});

export const getClientFlagsContextValue = (
  posthogFlags: PostHog,
): Record<keyof FlagContextType, boolean | undefined> => ({
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAdditionalExpressOnboardingOptions,
  ),
  EnableAgGridUpgrade: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAgGridUpgrade,
  ),
  EnableAiWorkspaceCustomization: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAiWorkspaceCustomization,
  ),
  EnableAlphaApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAlphaApps,
  ),
  EnableAppBuilder: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilder,
  ),
  EnableAppBuilderInspector: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilderInspector,
  ),
  EnableBetaApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableBetaApps,
  ),
  EnableCompanyImport: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableCompanyImport,
  ),
  EnableCompanyResetPasswords: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableCompanyResetPasswords,
  ),
  EnableContextualActions: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableContextualActions,
  ),
  EnableContractTemplatesPerf: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableContractTemplatesPerf,
  ),
  EnableIUAccessControl: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableIUAccessControl,
  ),
  EnableMicrodepositVerification: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMicrodepositVerification,
  ),
  EnableMessageAppNotification: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMessageAppNotification,
  ),
  EnableMessageEditorInput: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMessageEditorInput,
  ),
  EnableOtherMessagesEditor: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableOtherMessagesEditor,
  ),
  EnableDuplicateFileName: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableDuplicateFileName,
  ),
  EnableFileBatchActions: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableFileBatchActions,
  ),
  EnableFilesAppPerformance: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableFilesAppPerformance,
  ),
  EnableLoadFileChannels: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableLoadFileChannels,
  ),
  EnableNewEditorFormatter: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableNewEditorFormatter,
  ),
  EnableInvoiceAddressCustomFields: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableInvoiceAddressCustomFields,
  ),
  EnableInvoiceRefunds: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableInvoiceRefunds,
  ),
  EnableClientNotificationsMute: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableClientNotificationsMute,
  ),
  EnableAuditLogs: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAuditLogs,
  ),
  EnableEventsApiDateFilters: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableEventsApiDateFilters,
  ),
  EnableFeatureAppVisibility: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableFeatureAppVisibility,
  ),
  EnableMcpDashboardUi: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMcpDashboardUi,
  ),
  EnableIterateOnProjectGithubActions: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableIterateOnProjectGithubActions,
  ),
  EnableProductAgent: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableProductAgent,
  ),
  EnableAuditLogPlatform: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAuditLogPlatform,
  ),
  EnableConsistentAppSettingsLink: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableConsistentAppSettingsLink,
  ),
  EnableMultiInstanceApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMultiInstanceApps,
  ),
});

export const FlagsContext = createContext(DefaultFlagsContext);

/**
 * Custom hook to access feature flags from the context. This abstracts away the
 * underlying implementation and allows components to easily check if a feature is enabled.
 */
export function useFeatureFlags(): FlagContextType {
  return useContext(FlagsContext);
}
